import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ActionItem {
  /** Identificador único do item (usado como key da lista). */
  key: string;
  /** Texto exibido no item. */
  label: string;
  /** Ícone opcional exibido à esquerda do label. */
  icon?: React.ReactNode;
  /** Callback original da ação. */
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Item visível porém não clicável. */
  disabled?: boolean;
  /** Item não é renderizado (use para regras de permissão/negócio). */
  hidden?: boolean;
  /** Title (tooltip nativo). Por padrão usa o label. */
  title?: string;
  /** Classes extras do item (ex.: destaque destrutivo). */
  className?: string;
  /** Fecha o popover ao clicar. Padrão: true. */
  closeOnSelect?: boolean;
}

export interface ActionsPopoverProps {
  actions: ActionItem[];
  align?: "start" | "center" | "end";
  /** Title/aria-label do botão de ações. */
  triggerTitle?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Impede que o clique borbulhe para elementos clicáveis pais (ex.: <tr>). */
  stopPropagation?: boolean;
}

/**
 * Botão único (três pontos) que abre um Popover com a lista de ações.
 *
 * Não contém regra de negócio: quem monta a lista de ações é o consumidor,
 * reaproveitando seus próprios callbacks, permissões e validações.
 */
export function ActionsPopover({
  actions,
  align = "end",
  triggerTitle = "Ações",
  triggerClassName,
  contentClassName,
  stopPropagation = true,
}: ActionsPopoverProps) {
  const [open, setOpen] = React.useState(false);

  const visibleActions = React.useMemo(
    () => actions.filter((action) => !action.hidden),
    [actions]
  );

  if (visibleActions.length === 0) return null;

  const handleStopPropagation = (event: React.MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={triggerTitle}
          aria-label={triggerTitle}
          onClick={handleStopPropagation}
          className={cn(
            "inline-flex size-8 items-center justify-center rounded-md border border-input bg-background text-foreground transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
            triggerClassName
          )}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        onClick={handleStopPropagation}
        className={cn("w-56 p-1", contentClassName)}
      >
        <div className="flex flex-col">
          {visibleActions.map((action) => (
            <button
              key={action.key}
              type="button"
              disabled={action.disabled}
              title={action.title ?? action.label}
              onClick={(event) => {
                if (stopPropagation) event.stopPropagation();
                if (action.disabled) return;
                action.onClick(event);
                if (action.closeOnSelect !== false) setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:outline-none focus-visible:bg-accent focus-visible:text-accent-foreground",
                "disabled:pointer-events-none disabled:opacity-50",
                action.className
              )}
            >
              {action.icon && (
                <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
                  {action.icon}
                </span>
              )}
              <span className="flex-1 truncate">{action.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
